/**
 * Payslip drawer — the per-employee payslip doc, shown in a modal from a
 * "Payslip" link on the run grid (mirrors the prototype's `.drawer` + `.slip`).
 *
 * Earnings (gross) on the left, the five TYPED deductions on the right, net at
 * the foot. Honesty footnote: "Deduction amounts are as entered for this run.
 * TDS deposit + Form 16 are filed outside Flint (V1)." The PDF download is a
 * V1.x follow-on (Increment 3) — the button is disabled with a tooltip.
 *
 * @see prototype/payroll/payroll-run.html — payslip drawer
 */
import { Download, X } from "lucide-react";
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
} from "@flint/ui";
import { paiseToInrExact } from "../../../lib/format.ts";
import type { RunLineView } from "../lib/run-model.ts";

export interface PayslipDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: RunLineView | null;
  companyName: string;
  periodLabel: string;
}

export function PayslipDrawer({
  open,
  onOpenChange,
  line,
  companyName,
  periodLabel,
}: PayslipDrawerProps) {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent size="md" hideClose>
          {line ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-ink">Payslip · {periodLabel}</h2>
                <DialogClose
                  className="inline-grid h-8 w-8 place-items-center rounded-sm text-muted hover:bg-bg"
                  aria-label="Close"
                >
                  <X size={18} aria-hidden />
                </DialogClose>
              </div>

              <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
                {/* Letterhead */}
                <div className="flex items-start justify-between border-b border-line px-4 py-3">
                  <div>
                    <div className="font-semibold text-ink">{companyName}</div>
                  </div>
                  <div className="text-right text-xs text-muted">
                    Payslip
                    <div className="text-sm font-semibold text-ink">{periodLabel}</div>
                  </div>
                </div>

                {/* Employee */}
                <div className="grid grid-cols-2 gap-2 border-b border-line px-4 py-3 text-sm">
                  <Field k="Employee" v={line.employeeName} />
                  <Field k="Designation" v={line.designation ?? "—"} />
                </div>

                {/* Earnings + deductions */}
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="border-b border-line p-4 sm:border-b-0 sm:border-r">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                      Earnings
                    </h4>
                    <SlipRow label="Gross salary" value={paiseToInrExact(line.grossPaise)} />
                  </div>
                  <div className="p-4">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                      Deductions
                    </h4>
                    <SlipRow label="TDS (§192)" value={minus(line.tdsPaise)} />
                    <SlipRow label="Provident Fund" value={minus(line.pfPaise)} />
                    <SlipRow label="ESI" value={minus(line.esiPaise)} />
                    <SlipRow label="Professional Tax" value={minus(line.ptPaise)} />
                    <SlipRow label="Other" value={minus(line.otherDeductionPaise)} />
                  </div>
                </div>

                {/* Net */}
                <div className="flex items-center justify-between border-t-2 border-line-2 bg-sky-50 px-4 py-3">
                  <span className="font-semibold text-ink">Net pay</span>
                  <span className="font-mono text-xl font-bold text-sky-700">
                    {paiseToInrExact(line.netPaise)}
                  </span>
                </div>

                <div className="border-t border-line px-4 py-3 text-xs text-muted">
                  Deduction amounts are as entered for this run. TDS deposit + Form 16 are filed
                  outside Flint (V1).
                </div>
              </div>

              <button
                type="button"
                disabled
                title="Payslip PDF lands in Payroll Increment 3"
                className="inline-flex h-10 w-full cursor-not-allowed items-center justify-center gap-2 rounded-sm border border-line bg-paper text-sm font-semibold text-muted opacity-60"
              >
                <Download size={15} aria-hidden />
                Download PDF
                <span className="ml-1 rounded bg-bg px-1.5 py-0.5 text-xs font-semibold uppercase">
                  V1.x
                </span>
              </button>
            </div>
          ) : null}
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted">{k}</div>
      <div className="text-ink">{v}</div>
    </div>
  );
}

function SlipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-sm">
      <span className="text-ink-2">{label}</span>
      <span className="font-mono text-ink">{value}</span>
    </div>
  );
}

/** "− ₹1,800.00" for a deduction (₹0.00 shows plain). */
function minus(paise: string): string {
  const formatted = paiseToInrExact(paise);
  return paise === "0" ? formatted : `− ${formatted}`;
}
